import { MinOrderQtyType } from '@/enums/MinOrderQtyType';
import UserError from '@/error';
import i18n from '@/i18n';
import CartMenu from '@/interfaces/CartMenu';
import Menu from '@/interfaces/Menu';
import { useCommonStore } from '@/stores/common';
import { useConfigStore } from '@/stores/config';
import { useMenuStore } from '@/stores/menu';
import { useOrderStore } from '@/stores/order';
import { getNameByLocale } from '@/utils';

interface Result {
  /**
   * 유효성 검사 결과
   * true: 유효
   * false: 유효하지 않음
   */
  isValid: boolean;
  /** 유효하지 않은 경우 메뉴 이름 */
  name?: string;
  /** 유효하지 않은 경우 최소주문수량 */
  minQty: number;
}

/**
 * 최소 주문 수량 유효성 검사용 Composable 함수
 *
 * @returns {{
 *   validateMinOrderQty: (cartMenu: CartMenu) => Result;
 *   assertMinOrderQty: (cartMenus: CartMenu[]) => void;
 * }}
 * - validateMinOrderQty: 단일 장바구니 메뉴의 최소 주문 수량 유효성 검사 함수.
 * - assertMinOrderQty: 여러 장바구니 메뉴를 검사해 조건 위반시 Error를 throw 하는 함수.
 */
export function useMinOrderQtyValidator() {
  const t = i18n.global.t;
  const commonStore = useCommonStore();
  const configStore = useConfigStore();
  const menuStore = useMenuStore();
  const orderStore = useOrderStore();

  /**
   * 최소주문수량 유효성 검사 및 조정
   * @param cartMenu   장바구니 메뉴
   * @returns 메뉴 수량 변경한 경우 true, 변경하지 않은 경우 false
   */
  const adjustMinOrderQtyAsync = async (cartMenu: CartMenu): Promise<boolean> => {
    const result = validateMinOrderQty(cartMenu);

    // 유효성 검사 결과가 유효한 경우 아무것도 하지 않음
    if (result.isValid) return true;

    const isOK = await commonStore.showDialog({
      type: 'confirm',
      message: t('general.ask_change_menu_qty', { qty: result.minQty }),
    });

    if (isOK) {
      cartMenu.Qty = result.minQty;
      return true;
    }

    return false;
  };

  /**
   * 메뉴의 최소주문수량을 계산
   * @param menu  메뉴 데이터
   * @returns 최소주문수량
   */
  const calcMinOrderQty = (menu: Menu): number => {
    switch (configStore.config.MinOrderQtyType) {
      // 첫 주문 시에만 최소주문수량 사용
      case MinOrderQtyType.FirstOnly: {
        // 이미 주문이 완료된 메뉴의 수량 조회
        const orderedQty =
          orderStore.order?.Items?.find((orderItem) => orderItem.ITEM_CODE === menu.ItemCode)?.QTY || 0;
        return menu.MinOrderQty - orderedQty <= 0 ? 1 : menu.MinOrderQty - orderedQty;
      }

      // 항상 최소주문수량 사용
      case MinOrderQtyType.Always:
        return menu.MinOrderQty <= 0 ? 1 : menu.MinOrderQty;

      default:
        return 1;
    }
  };

  /**
   * 최소주문수량 조건을 위반하는 메뉴가 있는지 검사하고, 위반하는 경우 에러를 발생시킴
   * @param cartMenus  장바구니 메뉴 목록
   * @throws UserError  최소주문수량 조건을 위반하는 경우 에러 발생
   */
  const assertMinOrderQty = (cartMenus: CartMenu[]): void => {
    const invalidMenus = cartMenus
      .map(validateMinOrderQty)
      .filter((result) => !result.isValid)
      .map((result) => {
        return t('error.min_order_qty_insufficient', { name: result.name, qty: result.minQty });
      });

    if (invalidMenus.length > 0) {
      throw new UserError(invalidMenus.join('\n'));
    }
  };

  /**
   * 최소주문수량 유효성 검사 후 실패하는 경우 메뉴 이름과 최소주문수량 반환
   * @param cartMenu  장바구니 메뉴
   * @returns  최소주문수량 유효성 검사 결과
   */
  const validateMinOrderQty = (cartMenu: CartMenu): Result => {
    const result: Result = { isValid: true, minQty: 0 };

    // 최소주문수량 옵션 사용하지 않는 경우 true 리턴
    if (configStore.config.MinOrderQtyType === MinOrderQtyType.None) return result;

    // 장바구니 메뉴에 해당하는 메뉴 조회
    const menu = menuStore.getMenu(cartMenu.GroupCode, cartMenu.ItemCode);

    // 최소주문수량 옵션이 적용된 메뉴가 아닌 경우 true 반환
    if (menu.MinOrderQty === 0) return result;

    // 첫 주문에만 적용인 경우 현재 주문 완료된 메뉴에서 수량 조회
    const orderedMenuQty =
      configStore.config.MinOrderQtyType === MinOrderQtyType.FirstOnly
        ? orderStore.order.Items?.find((item) => item.ITEM_CODE === menu.ItemCode)?.QTY || 0
        : 0;

    // 주문완료된 메뉴 수량이 최소주문 수량 보다 크거나 같은 경우 true 반환
    if (menu.MinOrderQty <= orderedMenuQty + cartMenu.Qty) return result;

    // 이 외에는 실패
    return {
      isValid: false,
      name: getNameByLocale(menu),
      minQty: menu.MinOrderQty,
    } satisfies Result;
  };

  return {
    calcMinOrderQty,
    adjustMinOrderQtyAsync,
    assertMinOrderQty,
  };
}
